'use client';

import { List, ListItemButton, Radio, Stack, Typography } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';
import { ConditionOperatorEnum, Provider } from '@repo/api-gw-sdk';

import { CloudProviderIndicator } from '@/components/cloudProviderIndicator';
import { Loader } from '@/components/layout/loading';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { GoogleWorkspaceResourceType } from '@/data/inventory/resourceTypes';

import { StepContainer } from '../../wizard/StepContainer';
import type { GoogleWorkspaceRestoreState } from '../restoreGoogleWorkspaceWizard';

const workspaceFilter = {
  operator: ConditionOperatorEnum.In,
  property: 'resourceType',
  value: [GoogleWorkspaceResourceType],
};

export const SelectTargetWorkspaceStep = (
  props: StepProps<GoogleWorkspaceRestoreState>
) => {
  const { wizardState, setWizardState, stepperLabels, back, currentStep } =
    props;
  const dal = useDAL();
  const { data, isLoading } = dal.inventory.useList(
    { pageIndex: 0, pageSize: 100 },
    { id: 'resourceName', desc: false },
    workspaceFilter
  );

  const workspaces = data?.resources ?? [];

  const onWorkspaceSelected = (workspace: InventoryResource) => {
    setWizardState((state) => ({
      ...state,
      targetWorkspace: workspace,
    }));
  };

  return (
    <StepContainer
      stepperLabels={stepperLabels}
      stepperIndex={0}
      onBackClick={back}
      canGoNext={() => !!wizardState.targetWorkspace}
      onNextClick={async () => props.setNextStep(currentStep.next?.[0])}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={wizardState.snapshot?.pointInTime}
          vaultId={wizardState.snapshot?.vaultId}
        />
      }
    >
      <Typography component='span'>
        Select a workspace to restore to:
      </Typography>
      {isLoading ? (
        <Loader />
      ) : (
        <List className='mt-[20px] mb-[10px]'>
          {workspaces.map((workspace) => {
            const isChecked = wizardState.targetWorkspace?.id === workspace.id;
            return (
              <ListItemButton
                key={workspace.id}
                sx={{
                  border: isChecked
                    ? '2px solid var(--mui-palette-primary-main)'
                    : '1px solid var(--mui-palette-divider)',
                  padding: isChecked ? '16px 24px' : '17px 25px',
                  marginBottom: '8px',
                }}
                onClick={() => onWorkspaceSelected(workspace)}
              >
                <Radio
                  className='p-0'
                  checked={isChecked}
                  value={workspace.id}
                />
                <Stack
                  direction='row'
                  alignItems='center'
                  gap='12px'
                  sx={{ marginLeft: '16px' }}
                >
                  <CloudProviderIndicator
                    cloudProvider={Provider.Gcp}
                    text={
                      <Stack>
                        <Typography variant='body1'>
                          {workspace.resourceName}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {workspace.tags?.Domain}
                        </Typography>
                      </Stack>
                    }
                  />
                </Stack>
              </ListItemButton>
            );
          })}
        </List>
      )}
    </StepContainer>
  );
};
